/* =============================================================================
   [KI] Product Detail — hero CTA scroll-to-selector behavior
   =============================================================================
   The hero CTA is system-first: it doesn't commit the buyer to a specific
   SKU/price. Instead, clicking it scrolls the page down to the Protocol
   Selector section and flashes it briefly so the buyer knows they've arrived.

   Wiring contract:
   - hero CTA button carries  data-scroll-to="#protocol-selector"
   - the target element must exist on the page (id or any valid selector)
   - if the target lives inside an element with class `protocol-selector`,
     we add `ps-flash` to that ancestor for 1400ms to trigger the highlight
     animation defined in protocol-selector.css

   Defensive: attaches at DOMContentLoaded, tolerates zero or many buttons,
   no-ops cleanly if the target doesn't exist.
   ========================================================================== */
(function () {
  'use strict';

  var FLASH_MS = 1400;

  function onClick(e) {
    var btn = e.currentTarget;
    var sel = btn.getAttribute('data-scroll-to');
    if (!sel) return;

    var headingTarget;
    try {
      headingTarget = document.querySelector(sel);
    } catch (_) {
      return; /* bad selector — fail silent */
    }
    if (!headingTarget) return;

    e.preventDefault();

    /* Scroll to the SECTION (not the inner heading) so the eyebrow / title /
       subheading framing is the first thing the buyer sees. If the configured
       target is already a section root, we use it directly. */
    var section = (headingTarget.closest && headingTarget.closest('.protocol-selector')) || headingTarget;

    /* Compute sticky-header offset. Themes vary — inspect common selectors and
       take the tallest one whose computed position is fixed/sticky and sitting
       near the top of the viewport. Fall back to 0. */
    var offset = stickyHeaderOffset() + 16; /* +16 breathing room */
    var top = section.getBoundingClientRect().top + window.pageYOffset - offset;

    /* Custom smooth-scroll. The native behavior:'smooth' uses a linear-ish
       curve that spends proportional time crossing the tall pinned mechanism
       section (~340vh), where the sticky graphic doesn't visually move — this
       reads as the page "getting stuck" mid-scroll.

       Our replacement uses an asymmetric easing (fast out, slow in) and a
       distance-proportional duration that caps at 900ms regardless of how
       far the page has to travel. Net result: the mechanism section flies
       past too quickly for the eye to register a pin, and the scroll still
       decelerates gracefully onto the target. */
    /* Flash the selector section once the scroll lands, so the forced
       reflow (`void section.offsetWidth`) and any layout shifts from
       `ps-flash` don't compete with the scroll's first frame. */
    function fireFlash() {
      if (section.classList && section.classList.contains('protocol-selector')) {
        section.classList.remove('ps-flash');
        void section.offsetWidth;
        section.classList.add('ps-flash');
        window.setTimeout(function () {
          section.classList.remove('ps-flash');
        }, FLASH_MS);
      }
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      /* `behavior: 'instant'` overrides any CSS `scroll-behavior: smooth`
         on <html>. Without it, even the reduced-motion path animates. */
      window.scrollTo({ top: top, left: 0, behavior: 'instant' });
      fireFlash();
    } else {
      smoothScrollTo(top, fireFlash);
    }
  }

  /* Symmetric ease-in-out scroll. easeInOutCubic gives a gentle start
     (no first-frame jolt), a fast middle, and a soft landing — which is
     what users actually perceive as "smooth."

     The earlier easeOutQuart implementation front-loaded so aggressively
     that frame 1 covered ~7% of the distance instantly (e.g. ~420px on a
     6000px scroll), which the eye reads as a discrete pre-jump followed
     by a separate decelerating glide — not a single smooth motion.

     Duration is proportional to distance but capped at 900ms — short hops
     feel snappy, long ones don't feel interminable. The cap keeps even a
     full-page scroll past the 340vh pinned mechanism section under a
     second, which is well below the threshold where the pin's visual
     stillness would register as "stuck." */
  function smoothScrollTo(targetY, onComplete) {
    var startY = window.pageYOffset;
    var delta = targetY - startY;
    if (Math.abs(delta) < 2) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    var distance = Math.abs(delta);
    /* 0.6ms per px, clamped to [350ms, 900ms]. */
    var duration = Math.max(350, Math.min(900, distance * 0.6));
    var startTime = performance.now();

    function easeInOutCubic(t) {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function step(now) {
      var elapsed = now - startTime;
      var t = Math.min(1, elapsed / duration);
      var eased = easeInOutCubic(t);
      /* `behavior: 'instant'` is critical: it bypasses any CSS
         `scroll-behavior: smooth` set on <html>. Without it, the browser
         smooths each per-frame target independently, layering 60
         mini-animations per second on top of our RAF loop — which reads
         as a slow crawl during the scroll followed by a fast snap when
         our loop ends and the browser's last queued smoothing finishes. */
      window.scrollTo({ top: startY + delta * eased, left: 0, behavior: 'instant' });
      if (t < 1) {
        requestAnimationFrame(step);
      } else if (typeof onComplete === 'function') {
        onComplete();
      }
    }
    requestAnimationFrame(step);
  }

  /* Best-effort sticky header detection. Merchants can set
     `--pd-scroll-offset` on :root to override. */
  function stickyHeaderOffset() {
    var rootOverride = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--pd-scroll-offset')
    );
    if (!isNaN(rootOverride) && rootOverride > 0) return rootOverride;

    var candidates = document.querySelectorAll(
      'header.site-header, .site-header, .shopify-section-header-sticky, ' +
      '.header, .announcement-bar, .preview-bar, sticky-header'
    );
    var maxOffset = 0;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      var rect = el.getBoundingClientRect();
      /* Only count it if it's visually at the top (within 8px) */
      if (rect.top <= 8 && rect.height > maxOffset) {
        maxOffset = rect.height;
      }
    }
    return maxOffset;
  }

  /* ==========================================================================
     ECOMMERCE LAYOUT — hero duration picker
     --------------------------------------------------------------------------
     Wiring contract (rendered by ki-product-detail.liquid when the section's
     "PDP layout" setting is eCommerce):

       [data-pd-opts]      radiogroup wrapping the three cards
       [data-pd-opt]       one card; carries data-variant-id / data-price /
                           data-note / data-available
       [data-pd-form]      the Shopify product form
       [data-pd-variant]   hidden input[name="id"] — THE variant state
       [data-pd-price]     price span inside the CTA
       [data-pd-cta-label] label span inside the CTA
       [data-pd-note]      per-day + dosage line under the CTA (eCommerce)
       [data-pd-summary]   duration · price · per-day above the CTA (Plus)
       [data-pd-duration] / [data-pd-price-lead] / [data-pd-perday]
                           spans inside that summary (Plus)
       [data-pd-stock]     reassurance stock line, carries data-in/data-out (Plus)
       [data-pd-stock-text] / [data-pd-stock-dot]  its text and status dot (Plus)

     There is exactly ONE copy of the selected variant: the hidden input. The
     cards write to it, the CTA and the note read from it, and the form posts
     it. Nothing here keeps a parallel `selectedVariant` in JS.
     ========================================================================== */

  /* data-* money strings are written through Liquid's `escape`, so a shop
     whose money_format uses HTML entities (&euro;89,95) would otherwise leak
     the literal entity into textContent. Round-trip through a detached
     textarea to decode — .innerHTML on a textarea parses entities but cannot
     execute anything, so this is safe for merchant-authored strings. */
  var decoder;
  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) return str || '';
    if (!decoder) decoder = document.createElement('textarea');
    decoder.innerHTML = str;
    return decoder.value;
  }

  function initPicker(section) {
    var group = section.querySelector('[data-pd-opts]');
    if (!group || group.dataset.pdBound === '1') return;

    var form = section.querySelector('[data-pd-form]');
    var idInput = section.querySelector('[data-pd-variant]');
    if (!form || !idInput) return;

    group.dataset.pdBound = '1';

    var opts = Array.prototype.slice.call(group.querySelectorAll('[data-pd-opt]'));
    if (!opts.length) return;

    var cta = form.querySelector('[data-pd-cta]');
    var priceEl = form.querySelector('[data-pd-price]');
    var labelEl = form.querySelector('[data-pd-cta-label]');
    var noteEl = section.querySelector('[data-pd-note]');
    /* eCommerce Plus only. All null on the plain eCommerce layout, and every
       use below is guarded, so this file behaves identically there. */
    var durEl = section.querySelector('[data-pd-duration]');
    var leadEl = section.querySelector('[data-pd-price-lead]');
    var perDayEl = section.querySelector('[data-pd-perday]');
    var perDayWrapEl = section.querySelector('[data-pd-perday-wrap]');
    var unitEl = section.querySelector('[data-pd-unit]');
    var unitWrapEl = section.querySelector('[data-pd-unit-wrap]');
    var stockEl = section.querySelector('[data-pd-stock]');
    var stockTextEl = section.querySelector('[data-pd-stock-text]');
    var stockDotEl = section.querySelector('[data-pd-stock-dot]');
    var buyLabel = group.getAttribute('data-cta-label') || '';
    var soldOutLabel = group.getAttribute('data-sold-out-label') || '';

    function select(opt, moveFocus) {
      var id = opt.getAttribute('data-variant-id');
      if (!id) return;

      for (var i = 0; i < opts.length; i++) {
        var on = opts[i] === opt;
        /* classList.toggle's second argument is supported everywhere we
           care about; kept explicit for readability. */
        if (on) opts[i].classList.add('is-selected');
        else opts[i].classList.remove('is-selected');
        opts[i].setAttribute('aria-checked', on ? 'true' : 'false');
        /* Roving tabindex — the group is one tab stop, arrows move within it. */
        opts[i].tabIndex = on ? 0 : -1;
      }

      idInput.value = id;

      var available = opt.getAttribute('data-available') === 'true';
      var price = decodeEntities(opt.getAttribute('data-price'));
      var note = decodeEntities(opt.getAttribute('data-note'));

      if (priceEl) {
        priceEl.textContent = price;
        priceEl.hidden = !available;
      }
      if (labelEl) labelEl.textContent = available ? buyLabel : soldOutLabel;
      if (cta) cta.disabled = !available;
      if (noteEl) {
        noteEl.textContent = note;
        noteEl.hidden = !note;
      }

      /* ——— eCommerce Plus ———
         The summary restates the same selection the CTA commits to, so it is
         driven from the same data-* attributes rather than re-derived. */
      if (durEl) durEl.textContent = decodeEntities(opt.getAttribute('data-duration') || '');
      if (leadEl) leadEl.textContent = price;
      /* Unit price of the selected pack (Grundpreis) – follows the selection. */
      if (unitEl) {
        var unit = decodeEntities(opt.getAttribute('data-unit') || '');
        unitEl.textContent = unit;
        if (unitWrapEl) unitWrapEl.hidden = !unit;
      }
      if (perDayEl) {
        var perDay = decodeEntities(opt.getAttribute('data-perday') || '');
        perDayEl.textContent = perDay;
        /* Hide the whole group — separator, figure and suffix together — so a
           tier with no day count never leaves "· pro Tag" dangling. */
        if (perDayWrapEl) perDayWrapEl.hidden = !perDay;
      }

      /* Availability is per-variant: a sold-out tier must not keep showing a
         delivery promise left over from the tier selected before it. */
      if (stockEl && stockTextEl) {
        var inTxt = decodeEntities(stockEl.getAttribute('data-in') || '');
        var outTxt = decodeEntities(stockEl.getAttribute('data-out') || '');
        stockTextEl.textContent = available ? inTxt : outTxt;
        if (stockDotEl) {
          if (available) stockDotEl.classList.remove('is-out');
          else stockDotEl.classList.add('is-out');
        }
      }

      syncUrl(id);

      /* Additive hook for analytics / apps. The theme had no hero variant
         event before (the old hero CTA didn't commit to a variant), so
         nothing existing depends on it — but app pixels commonly listen. */
      dispatch(section, 'variant:change', { id: id, available: available, source: 'ki-product-detail' });

      if (moveFocus) opt.focus();
    }

    /* Keep ?variant= in sync so a refresh, a shared link or a back-button
       return restores the same card — the same contract Shopify's own
       variant pickers honour, and the reason the Liquid preselects
       product.selected_variant when it is present. */
    function syncUrl(id) {
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      } catch (_) { /* older browsers — the picker still works */ }
    }

    function move(from, delta) {
      var i = opts.indexOf(from);
      if (i === -1) return;
      var next = (i + delta + opts.length) % opts.length;
      select(opts[next], true);
    }

    opts.forEach(function (opt) {
      opt.addEventListener('click', function () { select(opt, false); });
    });

    /* Arrow / Home / End navigation, per the WAI-ARIA radiogroup pattern.
       Space and Enter already activate a <button>, so click covers those. */
    group.addEventListener('keydown', function (e) {
      var opt = e.target.closest ? e.target.closest('[data-pd-opt]') : null;
      if (!opt) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault(); move(opt, 1); break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault(); move(opt, -1); break;
        case 'Home':
          e.preventDefault(); select(opts[0], true); break;
        case 'End':
          e.preventDefault(); select(opts[opts.length - 1], true); break;
      }
    });

    /* Cart behaviour is NOT reimplemented here. The Protocol Selector already
       owns the AJAX add → theme-event → drawer-or-/cart-fallback path; we hand
       our form to it so both add-to-carts behave identically and there is one
       implementation to maintain. When that script isn't on the page (a PDP
       without the selector section), we don't preventDefault and the form
       posts natively — the theme's default product-form behaviour. */
    if (form.dataset.pdBound !== '1') {
      form.dataset.pdBound = '1';
      form.addEventListener('submit', function (e) {
        var ps = window.kiProtocolSelector;
        if (ps && typeof ps.submitForm === 'function') {
          e.preventDefault();
          ps.submitForm(form);
        }
      });
    }
  }

  /* ==========================================================================
     HERO FORMAT TOGGLE — Pulver / Kapseln
     --------------------------------------------------------------------------
     Powder and Capsules are two separate products, so the inactive segment is
     a NAVIGATION, not a state change. Same contract as the Protocol Selector's
     .ps-format control:

       [data-pd-format]      the segmented control
       .pd-format__opt       a segment; the inactive one carries
                             data-sibling-href pointing at the sibling PDP

     Preferred path is window.kiPdpSwap.swap() — fetch the sibling and replace
     #main-content in place, so the cart drawer, scroll position and page state
     all survive. Without that module we fall back to a real navigation, which
     is the same thing the Protocol Selector does.
     ========================================================================== */
  function initFormatToggle(section) {
    var group = section.querySelector('[data-pd-format]');
    if (!group || group.dataset.pdBound === '1') return;
    group.dataset.pdBound = '1';

    var opts = Array.prototype.slice.call(group.querySelectorAll('.pd-format__opt'));

    opts.forEach(function (opt) {
      var href = opt.getAttribute('data-sibling-href');
      /* The active segment has no href — clicking it is a deliberate no-op. */
      if (!href) return;

      opt.addEventListener('click', function () {
        var fmt = opt.getAttribute('data-format');

        /* Optimistic paint BEFORE the fetch resolves, so the control responds
           to the tap instantly even on a slow connection. The swapped-in DOM
           carries the correct server-rendered state and replaces this. */
        opts.forEach(function (o) {
          var on = o === opt;
          o.classList.toggle('is-active', on);
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        opt.setAttribute('aria-busy', 'true');

        /* Drives every [data-format-only] rule on the page (Verzehrempfehlung,
           product facts) in lockstep with the toggle instead of after the
           round-trip. kiPdpSwap sets the same value when the swap lands. */
        if (fmt) document.body.setAttribute('data-format', fmt);
        try { sessionStorage.setItem('ki:format-intent', fmt); } catch (_) { /* private mode */ }

        if (window.kiPdpSwap && typeof window.kiPdpSwap.swap === 'function') {
          window.kiPdpSwap.swap(href);
        } else {
          window.location.href = href;
        }
      });

      /* Hover and focus are high-confidence intent signals — warm the sibling
         so the click has less network left to pay for. */
      function warm() {
        if (window.kiPdpSwap && typeof window.kiPdpSwap.prefetch === 'function') {
          window.kiPdpSwap.prefetch(href);
        }
      }
      opt.addEventListener('mouseenter', warm);
      opt.addEventListener('focus', warm);
    });
  }

  function dispatch(target, name, detail) {
    try {
      target.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (_) { /* CustomEvent unsupported — the picker itself still works */ }
  }

  function init() {
    initScrollButtons();
    /* ^= so eCommerce Plus binds too — it shares the whole card/CTA
       mechanism and differs only in which elements exist to update. */
    var sections = document.querySelectorAll('.product-detail[data-layout^="ecommerce"]');
    for (var s = 0; s < sections.length; s++) {
      initPicker(sections[s]);
      initFormatToggle(sections[s]);
    }
  }

  function initScrollButtons() {
    var buttons = document.querySelectorAll('.product-detail [data-scroll-to]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      /* Per-button bound guard. After a fetch-and-swap (kiPdpSwap) the
         old buttons are removed from the DOM and the new ones are
         inserted without this flag — they get bound cleanly. The guard
         exists so a defensive double-call to init() (e.g. theme editor
         reload firing both shopify:section:load and our swap event)
         doesn't double-bind survivors. */
      if (btn.dataset.pdBound === '1') continue;
      btn.dataset.pdBound = '1';
      btn.addEventListener('click', onClick);
    }
  }

  /* Expose init for re-binding after a fetch-and-swap. The kiPdpSwap
     module calls this on every successful swap so the new hero CTA is
     wired up before the customer can interact with it. */
  window.kiProductDetail = window.kiProductDetail || {};
  window.kiProductDetail.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-bind after a section reload (changing the layout setting
     re-renders this section, which drops the data-pd-bound flags with it). */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.product-detail')) init();
  });
})();